import { History } from "@shopify/app-bridge/actions"

import AppBridge, { getAppLink } from "@/utils/appBridge"

/** 与 ThemeLayoutSelector 内 s-app-window 的 id 一致 */
export const THEME_LAYOUT_BACKDROP_APP_WINDOW_ID = "tracking-theme-layout-backdrop-app-window"
export const THEME_LAYOUT_SHOWCASE_APP_WINDOW_ID = "tracking-theme-layout-showcase-app-window"

/**
 * customize 弹窗壳的实现切换：
 * - `true`（默认）：用 `@shopify/app-bridge-react` 的 `<Modal variant="max">`（备用方案）
 * - `false`：用 `<s-app-window>` Polaris Web Component
 *
 * 选 Modal 的原因：`<s-app-window>` 的 `hide` 事件触发后 `History.dispatch(REPLACE)` 不生效，
 * URL 卡住，且 SaveBar 不会随窗口自动隐藏；`<Modal>` 的 `onHide` 回调 + 内嵌 `<SaveBar>` 行为更稳定。
 */
export const USE_THEME_LAYOUT_MODAL_HOST = true

export interface SAppWindowElement extends HTMLElement {
  src: string
  show: () => Promise<void>
  hide: () => Promise<void>
}

/**
 * 主文档直接打开 /tracking-page/customize* 时走 App Window 外壳；带此参数时表示在 s-app-window 的 iframe 内，只渲染编辑内容，避免再套一层 window。
 */
export const THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM = "themeLayoutAppWindow"

export const mergeSearchWithThemeLayoutAppWindowContent = (search: string): string => {
  const raw = search.startsWith("?") ? search.slice(1) : search
  const params = new URLSearchParams(raw)

  params.set(THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM, "1")

  return `?${params.toString()}`
}

export const buildThemeLayoutAppWindowSrc = (subPath: "customizeBackdrop" | "customizeShowcase", search: string) => {
  return `/tracking-page/${subPath}${mergeSearchWithThemeLayoutAppWindowContent(search)}`
}

/**
 * 从 search 中移除 `themeLayoutAppWindow`，用于 **主文档** 嵌入历史。
 * 若主文档 URL 仍带 `=1`，用户刷新会误判为 iframe 内容帧，无法挂载 `s-app-window`。
 */
export const stripThemeLayoutAppWindowContentFromSearch = (search: string): string => {
  const raw = search.startsWith("?") ? search.slice(1) : search
  const params = new URLSearchParams(raw)

  params.delete(THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM)
  const query = params.toString()

  return query ? `?${query}` : ""
}

/**
 * 嵌入环境下同步到 Shopify Admin 历史的 path：**不带** `themeLayoutAppWindow`。
 * 与 `buildThemeLayoutAppWindowSrc`（仅用于 `s-app-window` 的 `src`）区分使用。
 */
export const buildThemeLayoutAppWindowEmbeddedHistoryPath = (
  subPath: "customizeBackdrop" | "customizeShowcase",
  search: string,
) => {
  return `/tracking-page/${subPath}${stripThemeLayoutAppWindowContentFromSearch(search)}`
}

/**
 * 将 App Window 对应的主文档路径同步到 Shopify Admin 嵌入历史（与 pathnameHandle 使用同一套 History API）。
 * **不包含** `themeLayoutAppWindow=1`，以便原地刷新时仍走 `ThemeLayoutAppWindowHost` 并 `show()`。
 * 不调用 React Router navigate，避免主区域与弹窗 iframe 重复渲染同一子路由。
 */
export const pushThemeLayoutAppWindowToShopifyHistory = (subPath: "customizeBackdrop" | "customizeShowcase", search: string) => {
  if (!AppBridge.exist()) {
    return
  }

  const path = buildThemeLayoutAppWindowEmbeddedHistoryPath(subPath, search)
  const history = History.create(AppBridge.app)

  history.dispatch(History.Action.PUSH, getAppLink(path))
}
